import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Category as CategoryIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import qrLandingTemplateService, {
  extractQrTemplateError,
} from '../../../../services/qrLandingTemplateService';
import type { PublicInventoryItem } from '../../../../services/inventoryItemPublicService';
import PageHeader from '../../../../components/venue/PageHeader';
import {
  QR_LANDING_VARIABLES,
  GENERAL_PROVISIONS_STARTER_TEMPLATE,
  renderAndSanitizeQrLanding,
} from '../../../../utils/qrLandingTemplate';

/**
 * Sample item used for the live preview (no real wine required). logoUrl is
 * intentionally blank to demonstrate the missing-logo placeholder; the real
 * venue logo appears on the live page when Main Nota has one configured.
 */
const SAMPLE_ITEM: PublicInventoryItem = {
  shortId: 'A1B2C3',
  displayName: 'Domaine de la Côte La Côte 2021 750 ml',
  sizeValue: 750,
  sizeUnit: 'ml',
  salePrice: 8500, // minor units → $85.00
  salePriceCurrency: 'USD',
  wine: {
    wineName: 'Domaine de la Côte La Côte 2021',
    displayVintage: '2021',
    producer: 'Domaine de la Côte',
    region: 'Sta. Rita Hills',
    regionPath: 'United States / California / Sta. Rita Hills',
    country: 'United States',
    color: 'RED',
  },
  venue: { name: 'General Provisions', logoUrl: '' },
  qrLandingTemplate: null,
};

const QrLandingTemplateEditor: React.FC = () => {
  const theme = useTheme();
  const { venueId } = useParams<{ venueId: string }>();

  const [html, setHtml] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const tpl = await qrLandingTemplateService.get(venueId);
      setHtml(tpl.html);
      setIsActive(tpl.isActive);
    } catch (err) {
      setError(extractQrTemplateError(err));
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!venueId) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await qrLandingTemplateService.save(venueId, { html, isActive });
      setHtml(saved.html);
      setIsActive(saved.isActive);
      setToast('Template saved.');
    } catch (err) {
      setError(extractQrTemplateError(err));
    } finally {
      setSaving(false);
    }
  };

  // Live preview — rendered with sample data, sanitized, isolated in an iframe
  // so the template's own <style> can't leak into the admin UI.
  const previewHtml = useMemo(() => {
    try {
      return renderAndSanitizeQrLanding(html, SAMPLE_ITEM);
    } catch {
      return '';
    }
  }, [html]);

  const insertVariable = (name: string) => {
    setHtml(prev => `${prev}{{${name}}}`);
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        icon={<CategoryIcon />}
        title="QR Landing Page"
        subtitle="HTML/CSS template for this venue's public QR wine page (/i/:shortId). Only the variables below are supported; each is inserted safely (escaped). Scripts, event handlers and iframes are rejected. When inactive, the default page is shown."
        actions={
          <>
            <FormControlLabel
              control={<Switch checked={isActive} onChange={e => setIsActive(e.target.checked)} />}
              label={<Typography variant="body2">Active</Typography>}
            />
            <Button
              startIcon={<ResetIcon />}
              onClick={load}
              disabled={loading || saving}
              sx={{ textTransform: 'none' }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={loading || saving}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      />

      {/* Variable helper list */}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {QR_LANDING_VARIABLES.map(v => (
          <Chip
            key={v.name}
            label={`{{${v.name}}}`}
            size="small"
            variant="outlined"
            onClick={() => insertVariable(v.name)}
            title={`${v.label} — click to insert`}
            sx={{ fontFamily: 'monospace', fontSize: '0.72rem', cursor: 'pointer' }}
          />
        ))}
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'stretch' }}>
        {/* Editor */}
        <Paper sx={{ flex: '1 1 420px', p: 2, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" fontWeight={600}>Template HTML</Typography>
            <Button
              size="small"
              onClick={() => setHtml(GENERAL_PROVISIONS_STARTER_TEMPLATE)}
              sx={{ textTransform: 'none' }}
            >
              Insert starter template
            </Button>
          </Box>
          <TextField
            value={html}
            onChange={e => setHtml(e.target.value)}
            multiline
            minRows={18}
            fullWidth
            placeholder="<div>…</div>"
            slotProps={{ input: { sx: { fontFamily: 'monospace', fontSize: '0.8rem', alignItems: 'flex-start' } } }}
          />
        </Paper>

        {/* Live preview */}
        <Paper sx={{ flex: '1 1 320px', p: 2, borderRadius: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>Preview (sample data)</Typography>
          <Box
            component="iframe"
            title="QR landing preview"
            srcDoc={previewHtml}
            sx={{
              width: '100%',
              minHeight: 420,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              backgroundColor: '#fff',
            }}
          />
          <Typography variant="caption" color="text.disabled">
            The venue logo appears on the live page; it is blank in this sample preview.
          </Typography>
        </Paper>
      </Box>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default QrLandingTemplateEditor;
